import React, {useEffect, useState} from 'react'
import {PageTitle} from 'layout/master-layout'
import {useTranslation} from 'react-i18next'
import {
 createOrganisationApi,
 getOrganisationDetailsApi,
 updateOrganisationApi,
} from 'utils/api/auth/organisationService'
import {
 CreateOrganisationPayload,
 FormDataType,
} from 'models/ViewOrganisation'
import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import OrganisationFormFields from './OrganisationFormFields'

const initialFormData: FormDataType = {
 name: '',
 type: '',

 state: null,
 district: null,
 subDistrict: null,

 state_name: '',
 district_name: '',
 subDistrict_name: '',

 pan_number: '',
 gst_number: '',

 territory: null,
 territory_name: '',

 commercial_area: null,
 commercial_area_name: '',
 pincode: '',
 full_address: '',
}
const ViewOrganisationForm = () => {
 const {t} = useTranslation()
 const [formData, setFormData] =
  useState<FormDataType>(initialFormData)
 const [, setInitialData] = useState<FormDataType | null>(
  null
 )
 const [isEditing, setIsEditing] = useState(false)
 const [loading, setLoading] = useState(false)
 const navigate = useNavigate()
 const {id} = useParams()
 const isEdit = Boolean(id)

 useEffect(() => {
  const fetchDetails = async () => {
   if (!id) return

   const res = await getOrganisationDetailsApi(Number(id))

   if (res) {
    const formatted: FormDataType = {
     name: res.name ?? '',
     type: res.type ?? '',
     state: res.state_id ?? null,
     district: res.district_id ?? null,
     subDistrict: res.sub_district_id ?? null,
     territory: null,
     state_name: res.state ?? '',
     district_name: res.district ?? '',
     subDistrict_name: res.sub_district ?? '',
     pan_number: res.pan_number ?? '',
     gst_number: res.gstin_number ?? '',
     commercial_area: null,
     commercial_area_name: res.commercial_area ?? '',
     territory_name: res.territory ?? '',
     pincode: res.pin_code ?? '',
     full_address: res.full_address ?? '',
    }

    setFormData(formatted)
    setInitialData(formatted)
   }
  }

  void fetchDetails()
 }, [id])

 const validateForm = () => {
  toast.dismiss()

  if (!formData.name?.trim()) {
   toast.error('Organisation name is required')
   return false
  }

  if (!formData.type) {
   toast.error('Organisation type is required')
   return false
  }

  if (!formData.state) {
   toast.error('State is required')
   return false
  }

  if (!formData.district) {
   toast.error('District is required')
   return false
  }

  if (!formData.subDistrict) {
   toast.error('Sub district is required')
   return false
  }

  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/

  if (formData.pan_number) {
   const pan = formData.pan_number.trim()

   if (!panRegex.test(pan)) {
    toast.error('Invalid PAN format')
    return false
   }
  }

  return true
 }

 const handleSubmit = async () => {
  if (!validateForm()) return
  try {
   setLoading(true)

   let res

   if (isEdit) {
    const payload = {
     org_id: Number(id),
     name: formData.name,
     type: formData.type,
     category: formData.type,

     state: formData.state || null,
     district: formData.district || null,
     sub_district: formData.subDistrict || null,
     territory: formData.territory || null,
     pan_number: formData.pan_number,
     commercial_area: formData.commercial_area,
     gstin_number: formData.gst_number,
     full_address: formData.full_address,
     pin_code: formData.pincode,
    }

    res = await updateOrganisationApi(payload)
   } else {
    const payload: CreateOrganisationPayload = {
     name: formData.name,
     type: formData.type,
     category: formData.type,

     state: formData.state || null,
     district: formData.district || null,
     sub_district: formData.subDistrict || null,
     territory: formData.territory || null,
     pan_number: formData.pan_number,
     gstin_number: formData.gst_number,
     commercial_area: formData.commercial_area,
     full_address: formData.full_address,
     pin_code: formData.pincode,
    }

    res = await createOrganisationApi(payload)
   }
   if (res?.success) {
    navigate(-1)
   } else {
    toast.error(res?.message || 'Something went wrong')
   }
  } catch (err) {
   toast.error('Something went wrong')
  } finally {
   setLoading(false)
  }
 }

 return (
  <div className='container relative'>
   <div className='px-[72px] pt-6 pb-10 bg-white w-full min-h-full'>
    <PageTitle
     breadcrumbContainerClass={
      'flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0]'
     }
     breadcrumbs={[
      {
       title: t('breadCrumbs.home'),
       path: '/home-page',
       isActive: false,
      },
      {
       title: t('breadCrumbs.activityManagement'),
       path: '/activity-management',
       isActive: false,
      },
      {
       title: t('breadCrumbs.viewOrganisation'),
       path: '/activity-management/view-organisation',
       isActive: false,
      },
      {
       title: t('breadCrumbs.addOrganisation'),
       path: '',
       isActive: true,
      },
     ]}
     description='Welcome'
    >
     {t('festiveGreeting.title')}
    </PageTitle>

    <div className='bg-white rounded-lg p-6'>
     <OrganisationFormFields
      formData={formData}
      setFormData={setFormData}
      isEdit={isEdit}
      isEditing={isEditing}
     />

     <div className='mt-8 flex justify-end gap-8'>
      {isEdit && !isEditing && (
       <button
        onClick={() => setIsEditing(true)}
        className='bg-[#5B7EE5]  text-white px-7 py-2 rounded-lg'
       >
        Edit
       </button>
      )}
      <button
       onClick={handleSubmit}
       disabled={loading}
       className='bg-[#5B7EE5] text-white px-6 py-2 rounded-lg font-medium'
      >
       {loading ? 'Saving...' : 'Save'}
      </button>
     </div>
    </div>
   </div>
  </div>
 )
}

export default ViewOrganisationForm
